import { useCallback, useEffect, useState } from 'react';

import {
  toRegistrationDraft,
  type RegistrationFormValues,
} from '../features/auth/application/registrationForm';
import { useSession } from '../features/auth/application/useSession';
import { ConfirmIdentityPage } from '../pages/ConfirmIdentityPage';
import { PendingApprovalPage } from '../pages/PendingApprovalPage';
import { RefusedPage } from '../pages/RefusedPage';
import { RegisterPage, type BranchOption } from '../pages/RegisterPage';
import { SignInPage } from '../pages/SignInPage';
import { Card } from '../shared/components';

import { useServices } from './providers/servicesContext';
import { SignedInApp } from './screens/SignedInApp';
import styles from '../pages/AuthPages.module.css';

/**
 * Composition root (blueprint §3.1).
 *
 * Routing is driven by the session state machine rather than by URLs: which
 * screen someone belongs on is a property of who they are, not of where they
 * navigated. A URL router arrives with the application's own screens, once
 * there is more than one place a linked employee can be.
 */
export function App() {
  const { auth, links, organisation } = useServices();
  const session = useSession(auth, links);
  const [actionError, setActionError] = useState<string | null>(null);
  const [branches, setBranches] = useState<readonly BranchOption[]>([]);
  const [branchesError, setBranchesError] = useState<string | null>(null);

  // Registration offers the list; the profile shows the employee which branch
  // they are recorded against. Nobody signed out or refused needs it.
  const needsBranches =
    session.state.kind === 'needs_registration' || session.state.kind === 'ready';

  useEffect(() => {
    // Fetched only where it is actually used. Loading it for everyone would be
    // a read per sign-in for a list some people never see — small, but §11.5.1
    // counts reads nobody needs as a cost defect.
    if (!needsBranches) return;

    let cancelled = false;
    organisation
      .listActiveBranches()
      .then((list) => {
        if (cancelled) return;
        setBranches(list.map(({ id, nameAr }) => ({ id, nameAr })));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setBranchesError(error instanceof Error ? error.message : 'تعذّر تحميل قائمة الفروع.');
      });

    return () => {
      cancelled = true;
    };
  }, [needsBranches, organisation]);

  const handleRegister = useCallback(
    async (values: RegistrationFormValues) => {
      setActionError(null);

      const state = session.state;
      if (state.kind !== 'needs_registration') return;

      try {
        await links.submitRegistrationRequest(toRegistrationDraft(values, state.reason));
        await session.refresh();
      } catch (error) {
        setActionError(error instanceof Error ? error.message : 'تعذّر إرسال الطلب.');
      }
    },
    [links, session],
  );

  const handleConfirm = useCallback(
    async (candidateToken: string) => {
      setActionError(null);
      try {
        await links.confirmIdentity(candidateToken);
        await session.refresh();
      } catch (error) {
        setActionError(error instanceof Error ? error.message : 'تعذّر التأكيد.');
      }
    },
    [links, session],
  );

  const handleDecline = useCallback(
    async (candidateToken: string) => {
      setActionError(null);
      try {
        await links.declineIdentity(candidateToken);
        await session.refresh();
      } catch (error) {
        setActionError(error instanceof Error ? error.message : 'تعذّر إتمام العملية.');
      }
    },
    [links, session],
  );

  const handleSignOut = useCallback(() => {
    void session.signOut();
  }, [session]);

  switch (session.state.kind) {
    case 'loading':
      return (
        <div className={styles.page}>
          <Card>
            <p className={styles.body} aria-live="polite">
              جارٍ التحقق من حسابك…
            </p>
          </Card>
        </div>
      );

    case 'signed_out':
      return (
        <SignInPage
          onSignIn={() => void session.signIn()}
          busy={session.busy}
          errorAr={session.errorAr}
        />
      );

    case 'refused':
      return <RefusedPage messageAr={session.state.messageAr} onSignOut={handleSignOut} />;

    case 'needs_confirmation': {
      const { employeeName, candidateToken } = session.state;
      return (
        <ConfirmIdentityPage
          employeeName={employeeName}
          onConfirm={() => handleConfirm(candidateToken)}
          onDecline={() => handleDecline(candidateToken)}
          errorAr={actionError ?? session.errorAr}
        />
      );
    }

    case 'needs_registration':
      return (
        <RegisterPage
          reasonMessageAr={session.state.messageAr}
          branches={branches}
          onSubmit={handleRegister}
          onSignOut={handleSignOut}
          errorAr={actionError ?? branchesError}
        />
      );

    case 'pending_approval':
      return (
        <PendingApprovalPage
          email={session.user?.email ?? ''}
          onSignOut={handleSignOut}
          onRefresh={() => void session.refresh()}
          busy={session.busy}
        />
      );

    case 'ready':
      return (
        <SignedInApp
          roles={session.state.roles}
          ownUid={session.user?.uid ?? ''}
          branches={branches}
          onSignOut={handleSignOut}
        />
      );
  }
}
